<template>
  <div class="mall-order-detail">
    <div class="page">
      <MallHeader :title="'주문상세내역'" @back-click="goBack">
        <MallIconButton slot="right" :icon="shoppingIcon" :alt="'장바구니'" @click="goCart" />
      </MallHeader>

      <template v-if="order">
        <div class="mall-order-detail__number">
          <span class="mall-order-detail__number-label">주문번호</span>
          <span>{{ order.reception_number }}</span>
        </div>

        <MallOrderStatusSteps v-if="statusSteps" :steps="statusSteps" />

        <div class="mall-order-detail__products">
          <MallOrderDetailProducts
            :items="items"
            @add-all-click="addAllItems"
            @rebuy-click="openPurchaseSheet"
          />
        </div>

        <template v-if="delivery">
          <div class="mall-order-detail__thick-divider"></div>

          <!-- TODO: 주문 당시 배송지가 API에 없어 프로필의 현재 배송지를 보여줌 -->
          <div class="mall-order-detail__section">
            <MallSubTitle :label="'배송 정보'" />
            <MallInfoRow :label="'받는 분'" :value="delivery.name" />
            <MallInfoRow :label="'연락처'" :value="delivery.phone" />
            <MallInfoRow :label="'주소'" :value="delivery.addressLines" />
          </div>
        </template>

        <div class="mall-order-detail__thick-divider"></div>

        <div class="mall-order-detail__section">
          <MallOrderPayment
            :productAmount="order.total_cost"
            :discountAmount="order.total_cost - order.patient_cost"
            :shippingFee="null"
            :totalAmount="order.patient_cost"
          />
        </div>

        <div class="mall-order-detail__inquiry">
          <div class="mall-order-detail__divider"></div>
          <span class="mall-order-detail__inquiry-text">환불 및 주문 취소는 채널톡으로 문의해 주세요.</span>
          <div class="mall-order-detail__inquiry-button">
            <MallButton :label="'채널톡 문의하기'" :type="'soft'" @click="openChannelTalk" />
          </div>
        </div>
      </template>

      <!-- 주문을 찾지 못했거나 불러오지 못했을 때 -->
      <div v-else-if="isLoaded" class="mall-order-detail__empty">
        <span class="mall-order-detail__empty-title">{{ isLoadFailed ? '주문 정보를 불러오지 못했어요' : '주문 정보를 찾을 수 없어요' }}</span>
        <span class="mall-order-detail__empty-message">{{ isLoadFailed ? '잠시 후 다시 시도해 주세요.' : '이용내역에서 다시 선택해 주세요.' }}</span>
      </div>

      <MallPurchaseLayer
        :product="purchaseProduct"
        :profileId="profileId"
        :productMap="productMap"
        @close="purchaseProduct = null"
      />

      <!-- 모든 상품 담기에서 일부 상품을 담지 못했을 때 -->
      <MallConfirmPopup
        v-if="copyResult"
        :icon="warningIcon"
        :title="copyResult.title"
        :messageLines="copyResult.messageLines"
        :cancelLabel="'닫기'"
        :confirmLabel="'장바구니 확인'"
        @close="copyResult = null"
        @cancel="copyResult = null"
        @confirm="goCart"
      />

      <MallToast
        v-if="toast"
        :key="toast.key"
        :title="toast.title"
        :message="toast.message"
        @close="toast = null"
      />
    </div>
  </div>
</template>

<script>
import Constant from '@/constant'
import mall from '@/utilities/mall.js'
import MallButton from '@/components/new/atoms/MallButton.vue'
import MallIconButton from '@/components/new/atoms/MallIconButton.vue'
import MallSubTitle from '@/components/new/atoms/MallSubTitle.vue'
import MallHeader from '@/components/new/molecules/MallHeader.vue'
import MallInfoRow from '@/components/new/molecules/MallInfoRow.vue'
import MallConfirmPopup from '@/components/new/molecules/MallConfirmPopup.vue'
import MallToast from '@/components/new/molecules/MallToast.vue'
import MallOrderDetailProducts from '@/components/new/organisms/MallOrderDetailProducts.vue'
import MallOrderPayment from '@/components/new/organisms/MallOrderPayment.vue'
import MallOrderStatusSteps from '@/components/new/organisms/MallOrderStatusSteps.vue'
import MallPurchaseLayer from '@/components/new/organisms/MallPurchaseLayer.vue'

var ORDER_LIMIT = 100
// 서버 배송 상태(fulfillment_status) → 상품 상태 (운영 API와 같은 값: 승인 전 / 승인 완료)
var FULFILLMENT_STATUSES = {
  AWAITING_APPROVAL: 'waiting',
  PREPARING: 'preparing',
}
// 배송 단계 (key는 상품 상태)
// TODO: 배송중 / 배송완료는 서버에 상태가 없어 항상 0
var STATUS_STEPS = [
  { key: 'waiting', label: '배송대기' },
  { key: 'preparing', label: '상품준비중' },
  { key: 'shipping', label: '배송중' },
  { key: 'delivered', label: '배송완료' },
]

export default {
  name: 'MallOrderDetail',
  components: {
    MallButton,
    MallIconButton,
    MallSubTitle,
    MallHeader,
    MallInfoRow,
    MallConfirmPopup,
    MallToast,
    MallOrderDetailProducts,
    MallOrderPayment,
    MallOrderStatusSteps,
    MallPurchaseLayer,
  },
  data() {
    return {
      profileId: Number(this.$route.query.profileId) || null,
      receptionNumber: this.$route.query.receptionNumber || '',
      shoppingIcon: require('@/assets/mall/icn_shopping_line.svg'),
      warningIcon: require('@/assets/mall/img_warning_triangle_3d.png'),
      purchaseProduct: null,

      // 결제 이력 한 건 (주문 상세 API가 없어 결제 이력 목록에서 접수번호로 찾음)
      order: null,
      // [{ id, status, date, categoryLabel, name, image, price, quantity, canRebuy, product }]
      items: [],
      // 전체 상품 (key: 상품 id) - 재구매 시 장바구니 상품 종류 확인용
      productMap: {},
      // { name, phone, addressLines }
      delivery: null,
      isLoaded: false,
      isLoadFailed: false,
      isRequesting: false,
      // 모든 상품 담기 결과 안내 { title, messageLines }
      copyResult: null,
      toast: null,
    }
  },
  computed: {
    // 배송 단계별 상품 수 - 결제 이력 API가 배송 상태를 내려줄 때만 노출 (환불 건은 null이라 모두 0)
    statusSteps() {
      if (!this.order || !('fulfillment_status' in this.order)) return null
      return STATUS_STEPS.map(step => ({
        key: step.key,
        label: step.label,
        count: this.items.filter(item => item.status === step.key).length,
      }))
    },
  },
  mounted() {
    ChannelIO('shutdown')

    var token = mall.getAccessToken()
    if (!token) {
      mall.goLogin(this.$router)
      return
    }
    this.fetchOrderDetail(token)
  },
  methods: {
    fetchOrderDetail(token) {
      // 상품 정보 / 배송지는 없어도 주문 내용은 보여줌
      var catalogRequest = mall.fetchCatalog(this.$store, this.profileId, token).catch(() => null)
      var profileRequest = mall.fetchProfile(this.$store, this.profileId, token).catch(() => null)

      Promise.all([this.findOrder(token, 1), catalogRequest, profileRequest])
        .then(([order, catalog, profile]) => {
          if (order) {
            this.order = order
            this.items = this.toItems(order, catalog)
            this.productMap = catalog ? catalog.productMap : {}
            this.delivery = profile ? this.toDelivery(profile.profile) : null
          }
          this.isLoaded = true
        })
        .catch((err) => {
          if (mall.isAuthError(err)) {
            mall.goLogin(this.$router)
            return
          }
          this.isLoadFailed = true
          this.isLoaded = true
        })
    },
    // 결제 이력을 페이지 단위로 넘기며 접수번호가 같은 건을 찾음
    findOrder(token, page) {
      return this.$store.dispatch(Constant.FETCH_STORE_ORDERS, {
        profile_id: this.profileId,
        page: page,
        limit: ORDER_LIMIT,
        token: token,
      })
        .then((data) => {
          var orders = (data && data.orders) || []
          var order = orders.find(item => item.reception_number === this.receptionNumber)
          if (order) return order
          var hasNextPage = orders.length > 0 && page * ORDER_LIMIT < data.total_count
          return hasNextPage ? this.findOrder(token, page + 1) : null
        })
    },
    // 주문 상품에는 이름·수량만 있어 상품명으로 상품 정보(이미지·가격·카테고리)를 찾음
    // TODO: 주문 상품에 product_id가 내려오면 id로 찾기
    toItems(order, catalog) {
      // 배송 상태는 주문 단위라 모든 상품이 같은 상태 (배송 상태가 없으면 결제완료로 표시)
      var status = order.is_refund ? 'refunded' : (FULFILLMENT_STATUSES[order.fulfillment_status] || 'paid')
      var date = mall.formatDate(order.purchased_at)

      return (order.products || []).map((orderProduct, index) => {
        var product = catalog ? catalog.products.find(item => item.name === orderProduct.name) : null
        var view = product ? mall.toProductView(product, catalog.categories) : null
        return {
          id: index,
          status: status,
          date: date,
          categoryLabel: view ? view.categoryLabel : '',
          name: orderProduct.name,
          image: view ? view.image : mall.productImage(null),
          // TODO: 주문 당시 가격이 API에 없어 현재 판매가로 표시
          price: view ? view.price : null,
          quantity: orderProduct.quantity,
          // 판매 중인 상품만 재구매
          canRebuy: !!view,
          product: view,
        }
      })
    },
    toDelivery(profile) {
      if (!profile || !profile.address) return null
      var address = [profile.address, profile.detail_address].filter(value => value).join(' ')
      return {
        name: profile.name,
        phone: this.formatPhone(profile.phone_number),
        addressLines: profile.zip_code ? [address, '(' + profile.zip_code + ')'] : [address],
      }
    },
    // 01012345678 → 010-1234-5678
    formatPhone(phone) {
      var digits = String(phone || '').replace(/[^0-9]/g, '')
      if (digits.length === 11) return digits.replace(/(\d{3})(\d{4})(\d{4})/, '$1-$2-$3')
      if (digits.length === 10) return digits.replace(/(\d{3})(\d{3})(\d{4})/, '$1-$2-$3')
      return phone || ''
    },
    goBack() {
      mall.goBack(this.$router, this.$route, { path: '/mall/history', query: { profileId: this.profileId } })
    },
    goCart() {
      this.$router.push({ path: '/mall/cart', query: { profileId: this.profileId } })
    },
    // 주문한 모든 상품을 새 장바구니로 복사 (기존 장바구니는 비워지고 교체됨)
    addAllItems() {
      var token = mall.getAccessToken()
      if (!token) {
        mall.goLogin(this.$router)
        return
      }
      if (this.isRequesting) return
      this.isRequesting = true
      this.$store.dispatch(Constant.COPY_CART_FROM_ORDER, {
        reception_number: this.order.reception_number,
        profile_id: this.profileId,
        token: token,
      })
        .then((result) => {
          this.isRequesting = false
          var copyResult = this.toCopyResult(result)
          if (copyResult) {
            this.copyResult = copyResult
          } else {
            this.goCart()
          }
        })
        .catch((err) => {
          this.isRequesting = false
          if (mall.isAuthError(err)) {
            mall.goLogin(this.$router)
            return
          }
          this.toast = { key: Date.now(), title: '장바구니에 담지 못했어요.', message: mall.errorMessage(err) }
        })
    },
    // 담지 못했거나 수량을 줄인 상품이 있으면 안내 (모두 담겼으면 null)
    toCopyResult(result) {
      var excluded = (result && result.excluded) || []
      var adjusted = (result && result.adjusted) || []
      var copied = (result && result.copied) || []
      // 담긴 상품이 없으면 제외 사유가 없어도 안내 (빈 장바구니로 이동하지 않도록)
      if (copied.length === 0) {
        return {
          title: '장바구니에 담을 수 있는 상품이 없어요',
          messageLines: ['주문한 상품이 판매가 끝났거나', '지금은 담을 수 없는 상품이에요.'],
        }
      }
      if (excluded.length === 0 && adjusted.length === 0) return null

      var messageLines = []
      if (excluded.length > 0) {
        messageLines.push('판매가 끝났거나 담을 수 없는 상품 ' + excluded.length + '개는', '빼고 담았어요.')
      }
      if (adjusted.length > 0) {
        messageLines.push('최대 구매 수량을 넘은 상품 ' + adjusted.length + '개는', '수량을 줄여서 담았어요.')
      }
      return {
        title: '일부 상품만 장바구니에 담았어요',
        messageLines: messageLines,
      }
    },
    openPurchaseSheet(item) {
      this.purchaseProduct = item.product
    },
    openChannelTalk() {
      // TODO: 프로필 정보(이름, 연락처 등)를 함께 전달할지 확인
      ChannelIO('boot', {
        'pluginKey': '90e69520-1088-43c6-a1cc-3019437a9be8',
      })
      ChannelIO('showMessenger')
    },
  },
}
</script>

<style scoped>
  .mall-order-detail {
    display: flex;
    width: 100%;
    min-height: 100vh;
    justify-content: center;
    align-items: flex-start;
  }
  .page {
    width: 100%;
    min-height: 100vh;
    padding-bottom: 32px;
    background: #FFF;
    box-sizing: border-box;
  }
  .mall-order-detail__number {
    display: flex;
    padding: 16px;
    align-items: center;
    gap: 4px;
    color: var(--color-text-main);
    font-family: Pretendard;
    font-size: var(--font-size-sm);
    font-weight: var(--font-weight-medium);
    line-height: var(--line-height-normal);
    white-space: nowrap;
  }
  .mall-order-detail__number-label {
    color: #4E5968;
  }
  .mall-order-detail__products {
    padding: 16px 16px 32px 16px;
  }
  .mall-order-detail__thick-divider {
    height: 12px;
    background: #F2F4F6;
  }
  .mall-order-detail__section {
    display: flex;
    padding: 20px 16px;
    flex-direction: column;
    gap: 8px;
  }
  .mall-order-detail__inquiry {
    display: flex;
    padding: 0 16px;
    flex-direction: column;
    gap: 24px;
  }
  .mall-order-detail__divider {
    height: 1px;
    background: #F2F4F6;
  }
  .mall-order-detail__inquiry-text {
    color: #6B7684;
    font-family: Pretendard;
    font-size: var(--font-size-sm);
    font-weight: var(--font-weight-medium);
    line-height: var(--line-height-normal);
    text-align: center;
  }
  .mall-order-detail__inquiry-button {
    display: flex;
  }
  .mall-order-detail__empty {
    display: flex;
    padding: 160px 16px 0 16px;
    flex-direction: column;
    align-items: center;
    gap: 4px;
    font-family: Pretendard;
    font-weight: var(--font-weight-medium);
    text-align: center;
  }
  .mall-order-detail__empty-title {
    color: var(--color-text-main);
    font-size: var(--font-size-lg);
    font-weight: var(--font-weight-semibold);
    line-height: var(--line-height-normal);
  }
  .mall-order-detail__empty-message {
    color: #6B7684;
    font-size: var(--font-size-sm);
    line-height: var(--line-height-normal);
  }
  @media (min-width: 600px) {
    .page {
      width: 600px;
    }
  }
</style>



// WEBPACK FOOTER //
// src/page/new/MallOrderDetail.vue